import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
  Autocomplete,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ApiServices from "../../../Utils/ApiServices/ApiServices";
import { getDecryptedAccessToken } from "../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";

const AddDrug = () => {
  const accessToken = getDecryptedAccessToken();
  const [drugData, setDrugData] = useState({
    intervals: [],
    routes: [],
    units: [],
    forms: [],
    facilities: [],
  });

  const [formData, setFormData] = useState({
    name: "",
    ndc_number: "",
    drug_code: "",
    related_code: "",
    lot_number: "",
    barcode: "",
    expiration: "",
    manufacturer: "",
    size: "",
    form: null,
    unit: null,
    route: null,
    active: true,
    templates: [{ selector: "", dosage: "", period: null, quantity: "", refills: "", pkgqty: "" }],
    warehouse_levels: {},
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const endpoints = {
      intervals: "/apis/default/api/list/drug_interval",
      routes: "/apis/default/api/list/drug_route",
      units: "/apis/default/api/list/drug_units",
      forms: "/apis/default/api/list/drug_form",
      facilities: "/apis/default/api/facility",
    };

    Promise.all(
      Object.entries(endpoints).map(([key, url]) =>
        ApiServices.get(url, { Authorization: `Bearer ${accessToken}` })
          .then((res) => ({ key, data: key === "facilities" ? res.data.data : res.data }))
          .catch((error) => ({ key, data: [] }))
      )
    ).then((responses) => {
      const newData = responses.reduce((acc, { key, data }) => ({ ...acc, [key]: data }), {});
      setDrugData(newData);
      setFormData((prev) => ({
        ...prev,
        warehouse_levels: newData.facilities.reduce((acc, facility) => {
          acc[facility.option_id] = { min: "", max: "" };
          return acc;
        }, {}),
      }));
    });
  }, []);

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleTemplateChange = (index, key, value) => {
    const updatedTemplates = [...formData.templates];
    updatedTemplates[index][key] = value;
    setFormData({ ...formData, templates: updatedTemplates });
  };

  const handleWarehouseChange = (id, key, value) => {
    setFormData((prev) => ({
      ...prev,
      warehouse_levels: { ...prev.warehouse_levels, [id]: { ...prev.warehouse_levels[id], [key]: value } },
    }));
  };

  const validateFields = () => {
    const requiredFields = ["name", "ndc_number", "drug_code", "form", "unit", "route", "expiration"];
    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!formData[field]) newErrors[field] = "This field is required";
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateFields()) return;

    setLoading(true);
    const payload = {
      ...formData,
      form: formData.form?.option_id,
      unit: formData.unit?.option_id,
      route: formData.route?.option_id,
      templates: formData.templates.map((t) => ({ ...t, period: t.period?.option_id })),
    };

    try {
      await ApiServices.post("/apis/default/api/drug", payload, { Authorization: `Bearer ${accessToken}` });
      setSnackbar({ open: true, message: "Drug added successfully", severity: "success" });
      setFormData({
        name: "",
        ndc_number: "",
        drug_code: "",
        related_code: "",
        lot_number: "",
        barcode: "",
        expiration: "",
        manufacturer: "",
        size: "",
        form: null,
        unit: null,
        route: null,
        active: true,
        templates: [{ selector: "", dosage: "", period: null, quantity: "", refills: "", pkgqty: "" }],
        warehouse_levels: drugData.facilities.reduce((acc, facility) => {
          acc[facility.option_id] = { min: "", max: "" };
          return acc;
        }, {}),
      });
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to add drug", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ padding: 3 }}>
      <Typography variant="h5" gutterBottom>Add Drug</Typography>
      
      <Grid container spacing={2}>
        {["name", "ndc_number", "drug_code", "related_code", "lot_number", "barcode",  "manufacturer", "size"].map((field) => (
          <Grid item sm={4} xs={12} key={field}>
            <TextField
              fullWidth
              label={field.replace("_", " ").toUpperCase()}
              name={field}
              value={formData[field]}
              onChange={handleInputChange}
              error={!!errors[field]}
              helperText={errors[field]}
            />
          </Grid>
        ))}

        <Grid item sm={4} xs={12}>
  <TextField
    fullWidth
    label="Expiration"
    type="date"
    InputLabelProps={{ shrink: true }}
    value={formData.expiration}
    onChange={(e) => setFormData({ ...formData, expiration: e.target.value })}
    inputProps={{ min: new Date().toISOString().split("T")[0] }}
    error={!!errors.expiration}
    helperText={errors.expiration}
  />
</Grid>


        {["forms", "units", "routes"].map((key) => (
          <Grid item sm={4} xs={12} key={key}>
            <Autocomplete
              options={drugData[key] || []}
              getOptionLabel={(option) => option.title}
              onChange={(e, newValue) => setFormData({ ...formData, [key.slice(0, -1)]: newValue })}
              renderInput={(params) => <TextField {...params} label={key.replace("s", "").toUpperCase()} error={!!errors[key.slice(0, -1)]} />}
            />
          </Grid>
        ))}

        <Grid item sm={4} xs={12}>
          <FormControlLabel
            control={<Checkbox checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />}
            label="Active"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3 }}>Templates</Typography>
      {formData.templates.map((template, index) => (
        <Grid container spacing={2} key={index}>
          {["selector", "dosage", "quantity", "refills", "pkgqty"].map((key) => (
            <Grid item sm={4} xs={12} key={key}>
              <TextField fullWidth label={key.toUpperCase()} value={template[key]} onChange={(e) => handleTemplateChange(index, key, e.target.value)} />
            </Grid>
          ))}
          <Grid item sm={4} xs={12}>
            <Autocomplete
              options={drugData.intervals || []}
              getOptionLabel={(option) => option.title}
              onChange={(e, newValue) => handleTemplateChange(index, "period", newValue)}
              renderInput={(params) => <TextField {...params} label="Period" />}
            />
          </Grid>
        </Grid>
      ))}

      <Typography variant="h6" sx={{ mt: 3 }}>Warehouse Levels</Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead><TableRow><TableCell>Facility</TableCell><TableCell>Min</TableCell><TableCell>Max</TableCell></TableRow></TableHead>
          <TableBody>
            {drugData.facilities.map(({ id, name,facility_type }) => (
              <TableRow key={id}>
                <TableCell>{name} - {facility_type}</TableCell>
                {["min", "max"].map((key) => (
                  <TableCell key={key}><TextField value={formData.warehouse_levels[id]?.[key] || ""} onChange={(e) => handleWarehouseChange(id, key, e.target.value)} /></TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading} sx={{ mt: 3 }}>Submit</Button>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Paper>
  );
};

export default AddDrug;
